import React from "react";
import { FiCheck, FiX } from "react-icons/fi";
import Button from "../../BaseUI/Button";
import ApprovalRejectModal from "../ApprovalRejectModal";
import ApprovalAttachmentModal from "../ApprovalAttachmentModal";
import ApprovalRowActions from "../ApprovalRowActions";
import { useApprovalActions } from "../../../hooks/useApprovalActions";
import { getApprovalPlugin } from "../../../approvals/registry";

const defaultSubjectLabel = (item, plugin) =>
  plugin?.getTitle?.(item) ?? item?.orderName ?? item?.title ?? "";

/** Approve/reject buttons for a custom entity modal footer. */
export const ApprovalEntityFooter = ({
  actions,
  item,
  canWrite = false,
  onReject,
  approveLabel,
  rejectLabel,
}) => {
  if (!item || !canWrite || !actions?.canActOn(item)) return null;
  const { approve, startReject, isActionLoading, stageConfig } = actions;

  return (
    <>
      <Button
        appearance="primary-subtle"
        icon={<FiCheck />}
        onClick={() => approve(item)}
        loading={isActionLoading(item, "approve")}
      >
        {approveLabel ??
          item?.currentApproval?.approveActionLabel ??
          stageConfig?.approveLabel ??
          "Onayla"}
      </Button>
      <Button
        appearance="danger-subtle"
        icon={<FiX />}
        onClick={() => {
          startReject(item);
          onReject?.(item);
        }}
      >
        {rejectLabel ??
          item?.currentApproval?.rejectActionLabel ??
          stageConfig?.rejectLabel ??
          "Reddet"}
      </Button>
    </>
  );
};

/** Row icons wired to entity approval actions. */
export const ApprovalEntityRowActions = ({
  actions,
  item,
  canWrite = false,
  onView,
  actionsPosition = "after",
  children,
}) => {
  if (!actions) return null;
  const { canActOn, approve, startReject, isActionLoading } = actions;
  const canAct = canWrite && canActOn(item);

  return (
    <ApprovalRowActions
      canAct={canAct}
      actionsPosition={actionsPosition}
      onView={onView}
      onApprove={() => approve(item)}
      onReject={() => startReject(item)}
      approveLoading={isActionLoading(item, "approve")}
    >
      {children}
    </ApprovalRowActions>
  );
};

/**
 * Entity-page approval shell: hook + reject modal.
 * Page supplies table/modal content; children receive `actions`.
 */
const ApprovalEntity = ({
  type,
  stage = "user",
  canWrite = false,
  onNotify,
  onAfterSuccess,
  getSubjectLabel,
  rejectVariant = "confirm",
  reasonPlaceholder,
  children,
}) => {
  const actions = useApprovalActions({
    type,
    stage,
    canWrite,
    onNotify,
    onAfterSuccess,
  });

  const {
    rejectTarget,
    rejectReason,
    setRejectReason,
    cancelReject,
    confirmReject,
    isActionLoading,
    stageConfig,
    plugin,
    attachmentTarget,
    cancelAttachment,
    confirmAttachment,
  } = actions;

  const subjectLabel =
    rejectTarget &&
    (getSubjectLabel?.(rejectTarget) ??
      defaultSubjectLabel(rejectTarget, plugin ?? getApprovalPlugin(type)));

  return (
    <>
      {typeof children === "function" ? children(actions) : children}
      <ApprovalRejectModal
        isOpen={!!rejectTarget}
        onClose={cancelReject}
        title={
          rejectTarget?.currentApproval?.rejectModalTitle ||
          stageConfig?.rejectModalTitle ||
          "Reddet"
        }
        subjectLabel={subjectLabel}
        reason={rejectReason}
        onReasonChange={setRejectReason}
        onConfirm={confirmReject}
        loading={rejectTarget && isActionLoading(rejectTarget, "reject")}
        variant={rejectVariant}
        reasonPlaceholder={reasonPlaceholder}
      />
      <ApprovalAttachmentModal
        isOpen={!!attachmentTarget}
        onClose={cancelAttachment}
        title={
          attachmentTarget?.currentApproval?.approveModalTitle ||
          stageConfig?.approveLabel ||
          "Onayla"
        }
        loading={
          attachmentTarget && isActionLoading(attachmentTarget, "approve")
        }
        onConfirm={confirmAttachment}
      />
    </>
  );
};

export default ApprovalEntity;
